'use client';

/**
 * StatsBreakdown
 *
 * Renders the structured payload returned by `case_management_get_stats`.
 * Compose existing StatCard primitives in a responsive grid.
 *
 * Issue #1432 Phase 2 — Wave 2 P1
 */

import Box from '@mui/material/Box';
import { z } from '@ops-matrix/shared-types';
import type { ReactElement } from 'react';
import { useMemo } from 'react';
import { useI18n } from '../../hooks/useI18n';
import { StatCard, type StatCardColor } from '../StatCard';
import { RendererEmptyState } from './RendererEmptyState';
import type { AstorToolRendererProps } from './types';

// ============================================================================
// Schema
// ============================================================================

const StatsSchema = z.object({
  totalCases: z.number().optional(),
  active: z.number().optional(),
  pending: z.number().optional(),
  completed: z.number().optional(),
  rejected: z.number().optional(),
  avgDaysToClose: z.number().optional(),
});

type Stats = z.infer<typeof StatsSchema>;

const StatsWrapperSchema = z.object({
  stats: StatsSchema,
});

function parseStatsData(data: unknown): Stats | null {
  const wrapped = StatsWrapperSchema.safeParse(data);
  if (wrapped.success) return wrapped.data.stats;

  const direct = StatsSchema.safeParse(data);
  if (direct.success) return direct.data;

  return null;
}

// ============================================================================
// Stat tiles
// ============================================================================

interface StatTileSpec {
  key: keyof Stats;
  titleKey: string;
  color: StatCardColor;
  formatValue: (n: number) => string;
}

// ============================================================================
// Component
// ============================================================================

export const StatsBreakdown = ({ data }: AstorToolRendererProps): ReactElement => {
  const { t, formatNumber } = useI18n();
  const stats = useMemo(() => parseStatsData(data), [data]);

  const tiles: StatTileSpec[] = useMemo(
    () => [
      {
        key: 'totalCases',
        titleKey: 'astorTools.stats.totalCases',
        color: 'primary',
        formatValue: formatNumber,
      },
      {
        key: 'active',
        titleKey: 'astorTools.stats.active',
        color: 'info',
        formatValue: formatNumber,
      },
      {
        key: 'pending',
        titleKey: 'astorTools.stats.pending',
        color: 'warning',
        formatValue: formatNumber,
      },
      {
        key: 'completed',
        titleKey: 'astorTools.stats.completed',
        color: 'success',
        formatValue: formatNumber,
      },
      {
        key: 'rejected',
        titleKey: 'astorTools.stats.rejected',
        color: 'error',
        formatValue: formatNumber,
      },
      {
        key: 'avgDaysToClose',
        titleKey: 'astorTools.stats.avgDaysToClose',
        color: 'secondary',
        formatValue: (n) => formatNumber(Math.round(n * 10) / 10),
      },
    ],
    [formatNumber],
  );

  if (!stats) {
    return (
      <RendererEmptyState title={t('astorTools.stats.empty')} description={t('astorTools.stats.emptyDescription')} />
    );
  }

  // Filter to tiles with values present (don't show "0 — 0 — 0 — 0")
  const visibleTiles = tiles.filter((tile) => typeof stats[tile.key] === 'number');

  if (visibleTiles.length === 0) {
    return (
      <RendererEmptyState title={t('astorTools.stats.empty')} description={t('astorTools.stats.emptyDescription')} />
    );
  }

  return (
    <Box
      sx={{
        my: 1.5,
        display: 'grid',
        gap: 1.5,
        gridTemplateColumns: {
          xs: '1fr',
          sm: 'repeat(2, 1fr)',
          md: 'repeat(3, 1fr)',
          lg: 'repeat(4, 1fr)',
        },
      }}
    >
      {visibleTiles.map((tile) => {
        const raw = stats[tile.key];
        const value = typeof raw === 'number' ? tile.formatValue(raw) : '—';
        return <StatCard key={tile.key} title={t(tile.titleKey)} value={value} color={tile.color} />;
      })}
    </Box>
  );
};

export default StatsBreakdown;
